import { type KeyboardEvent, useEffect, useRef, useState } from "react";

import type { Ingredient } from "@sytofood/shared";

import { type ParseMealView, api } from "../lib/api";
import {
  mealAddMissingIngredientEnabled,
  mealAddMissingItemEnabled,
  mealItemSwapEnabled,
} from "../lib/flags";
import { useLang } from "../lib/i18n";
import { mealsCopy } from "../lib/i18n/meals";
import { useProfile } from "../lib/profile-context";
import { ScreenShell } from "./screen-shell";
import { IconStar } from "./ui/icon";
import { AiFooter, Conf, H1, MonoMeta, TopBar, confLevelFromScore } from "./ui/primitives";

type Props = {
  householdId: string;
  parsed: ParseMealView;
  onSaved: () => void;
  onCancel: () => void;
};

type Item = ParseMealView["items"][number];

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

// Rescales kcal + macros by a portion change factor (new grams / old grams).
function rescaleNutrition<
  T extends { kcal: number; proteinG: number | null; fatG: number | null; carbsG: number | null },
>(item: T, factor: number): T {
  return {
    ...item,
    kcal: Math.round(item.kcal * factor),
    proteinG: item.proteinG !== null ? round1(item.proteinG * factor) : null,
    fatG: item.fatG !== null ? round1(item.fatG * factor) : null,
    carbsG: item.carbsG !== null ? round1(item.carbsG * factor) : null,
  };
}

// A dish total is the sum of its ingredients (ADR-0010).
function dishTotals(ings: Ingredient[]): {
  kcal: number;
  proteinG: number;
  fatG: number;
  carbsG: number;
} {
  return ings.reduce(
    (a, x) => ({
      kcal: a.kcal + x.kcal,
      proteinG: a.proteinG + (x.proteinG ?? 0),
      fatG: a.fatG + (x.fatG ?? 0),
      carbsG: a.carbsG + (x.carbsG ?? 0),
    }),
    { kcal: 0, proteinG: 0, fatG: 0, carbsG: 0 },
  );
}

// Build the short macro string per item ("B 6 · T 4 · W 38"). Mirrors
// B_Confirm in screens-b.jsx; null macros are dropped.
function macroString(item: Item): string {
  const parts: string[] = [];
  if (item.proteinG !== null) parts.push(`B ${Math.round(item.proteinG)}`);
  if (item.fatG !== null) parts.push(`T ${Math.round(item.fatG)}`);
  if (item.carbsG !== null) parts.push(`W ${Math.round(item.carbsG)}`);
  return parts.join(" · ");
}

function portionString(item: Item): string | null {
  if (item.portionGrams === null) return null;
  // Barcode for a drink resolves with portionUnit="ml" (OFF / FatSecret carry
  // the unit on the metric serving). Everything else defaults to grams.
  return `${item.portionGrams} ${item.portionUnit ?? "g"}`;
}

export function MealConfirmationScreen({ householdId, parsed, onSaved, onCancel }: Props) {
  const { lang } = useLang();
  const t = mealsCopy[lang];
  // ADR-0018: when the active profile is a child, attach childId to the
  // save payload so the meal is logged against that child instead of the
  // adult member. Parse / source flow is unchanged — only saveMeals carries
  // the field.
  const { active } = useProfile();
  const [items, setItems] = useState<Item[]>(parsed.items);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedFavIdx, setSavedFavIdx] = useState<Set<number>>(new Set());
  const [savingFavIdx, setSavingFavIdx] = useState<number | null>(null);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  // Which ingredient is in swap mode, plus its draft new name.
  const [swap, setSwap] = useState<{ itemIdx: number; ingIdx: number; text: string } | null>(
    null,
  );
  const [swapping, setSwapping] = useState(false);
  // "Add a missing item" affordance — collapsed chip by default; expands to
  // an input row when the user taps it. Flagged via ADR-0017.
  const addEnabled = mealAddMissingItemEnabled();
  const [addOpen, setAddOpen] = useState(false);
  const [addText, setAddText] = useState("");
  const [adding, setAdding] = useState(false);
  // Hard-cap manual adds per confirmation session. Pairs with aiGuard on the
  // server: prevents a single confused session from burning a meaningful
  // slice of the daily cost cap before the server-side ceiling kicks in.
  const [addCount, setAddCount] = useState(0);
  // Client-side throttle (gotcha: aiGuard protects the server, but a fast
  // tap-spam can still queue requests; this short ref blocks submits within
  // 500ms of the last one).
  const lastAddAtRef = useRef<number>(0);
  // Portion value captured on focus, so a blur can rescale kcal/macros by
  // the net change instead of compounding on every keystroke.
  const portionBaseRef = useRef<number | null>(null);
  // Max manual adds per confirmation. Picked to cover "I forgot the sos +
  // the bun + the coffee" without enabling abuse; aligns with the server's
  // 5/30s aiGuard burst window.
  const MAX_MANUAL_ADDS = 5;
  // Throttle window between submits, ms. Same intent as MAX_MANUAL_ADDS.
  const ADD_THROTTLE_MS = 500;
  // Hard cap on the total items in a single save, mirroring server-side
  // MealCreateSchema.items.max(20). Without this, a parse returning multiple
  // items (parser can return up to 20) on top of an already-populated list
  // would push past 20 and the save would 400 with a Zod error the user
  // could not recover from in the UI.
  const MAX_ITEMS = 20;
  // True when the last addMissingItem failed via the AI path (network /
  // 5xx / rate-limit / aiGuard block). Surfaces a "wpisz ręcznie" link in
  // the error pill so a user hit by rate-limit isn't completely blocked.
  // Cleared on any subsequent successful add or when the affordance is
  // collapsed.
  const [addFailedAi, setAddFailedAi] = useState(false);
  // Transient notice shown after a multi-item parse ("Added 3 items"). Keeps
  // the user aware that "kawa i bułka" produced multiple rows; without it
  // the UI silently dropped items[1..n] in the original design.
  const [addNotice, setAddNotice] = useState<string | null>(null);
  // Auto-clear the multi-item notice so it does not linger after subsequent
  // mutations (the original review flagged it as stale-able).
  useEffect(() => {
    if (!addNotice) return;
    const handle = setTimeout(() => setAddNotice(null), 6000);
    return () => clearTimeout(handle);
  }, [addNotice]);

  // "Add a missing ingredient" affordance, mirrors addMissingItem
  // one-to-one but lives inside a single dish's inline editor and appends
  // to item.ingredients[] (re-summing the dish total via dishTotals,
  // ADR-0010). Only one dish-input is open at a time, scoped by itemIdx
  // (parallels the editingIdx single-open pattern). Flagged via
  // mealAddMissingIngredient.
  const addIngEnabled = mealAddMissingIngredientEnabled();
  // The dish index whose ingredient-add input is currently open, or null.
  const [addIngFor, setAddIngFor] = useState<number | null>(null);
  const [addIngText, setAddIngText] = useState("");
  const [addingIng, setAddingIng] = useState(false);
  // Per-dish counter: lets the user add up to MAX_INGREDIENT_ADDS per dish
  // without coupling to the top-level addCount (a session-wide cap on
  // missing items would block legitimate ingredient adds across dishes).
  const [addIngCount, setAddIngCount] = useState<Map<number, number>>(new Map());
  const lastIngAddAtRef = useRef<number>(0);
  const [addIngFailedAi, setAddIngFailedAi] = useState(false);
  const [addIngNotice, setAddIngNotice] = useState<string | null>(null);
  // Max manual ingredient adds per dish, per confirmation. Same intent as
  // MAX_MANUAL_ADDS but scoped per dish (a 5-ingredient missed-component
  // window covers the realistic "I forgot oliwa + ser + sól" case).
  const MAX_INGREDIENT_ADDS = 5;
  const ADD_ING_THROTTLE_MS = 500;
  // Hard cap, mirrors NestedMealItemSchema.ingredients.max(30). Without
  // this, a parse returning multiple ingredients on top of a near-full list
  // would 400 on save with a Zod error.
  const MAX_INGREDIENTS = 30;
  // Auto-clear the multi-ingredient notice, parallel to addNotice.
  useEffect(() => {
    if (!addIngNotice) return;
    const handle = setTimeout(() => setAddIngNotice(null), 6000);
    return () => clearTimeout(handle);
  }, [addIngNotice]);

  // Per-flat-item AI swap. REPLACES items[idx] with a freshly parsed
  // estimate (e.g. "Jajka kurze surowe" -> "Jajka gotowane"). Distinct from
  // the ingredient-level `swap` state above: that one carries {itemIdx,
  // ingIdx, text} for nested dishes; this one is just a single itemIdx
  // because (a) swap is replace-not-append so no MAX_ITEMS risk, (b) only
  // shown when item.ingredients is empty so the two cannot coexist on the
  // same row by construction.
  const itemSwapEnabled = mealItemSwapEnabled();
  const [itemSwapFor, setItemSwapFor] = useState<number | null>(null);
  const [itemSwapText, setItemSwapText] = useState("");
  const [itemSwapping, setItemSwapping] = useState(false);
  const [itemSwapFailedAi, setItemSwapFailedAi] = useState(false);
  const lastItemSwapAtRef = useRef<number>(0);
  // Item indices whose CURRENT name was produced by a per-item swap (AI or
  // manual). Telemetry in handleSave matches finalItem.name against
  // parsed.items[].name; a swapped item's name belongs to a different food
  // than what was originally parsed, so any incidental name-collision with
  // another parsed item would emit a misleading aiKcal feedback row. We
  // skip these indices in the feedback loop, mirroring the manually-added
  // skip (manually-added items are absent from parsed.items so they skip
  // by lookup-miss; swapped items must skip by explicit set lookup).
  const swappedIdxRef = useRef<Set<number>>(new Set());
  // Throttle window between item-swap submits, ms. Mirrors ADD_THROTTLE_MS;
  // server aiGuard already caps cost, this just blocks tap-spam queueing.
  const ITEM_SWAP_THROTTLE_MS = 500;

  const totals = items.reduce(
    (acc, it) => ({
      kcal: acc.kcal + it.kcal,
      proteinG: acc.proteinG + (it.proteinG ?? 0),
      fatG: acc.fatG + (it.fatG ?? 0),
      carbsG: acc.carbsG + (it.carbsG ?? 0),
    }),
    { kcal: 0, proteinG: 0, fatG: 0, carbsG: 0 },
  );

  function removeItem(idx: number) {
    setItems(items.filter((_, i) => i !== idx));
    setEditingIdx(null);
    // swappedIdxRef holds INDICES, so removing one shifts everything
    // above down by one. Without this, the telemetry skip in handleSave
    // would target the wrong row after a remove (MEDIUM review issue #3):
    // swapping C at idx=2 then removing B at idx=1 used to keep {2} in
    // the set even though C is now at idx=1.
    const oldSwapped = swappedIdxRef.current;
    if (oldSwapped.size > 0) {
      const shifted = new Set<number>();
      for (const i of oldSwapped) {
        if (i === idx) continue;
        shifted.add(i > idx ? i - 1 : i);
      }
      swappedIdxRef.current = shifted;
    }
    // Drop the per-dish ingredient-add counter for the removed index. Without
    // this, the Map keys reference dishes that no longer exist at that idx
    // (review issue #2); a later add at the same idx would inherit a stale
    // count. Keys above `idx` still drift but the cap is per-session and per
    // realistic-dish, not security-critical.
    setAddIngCount((prev) => {
      if (!prev.has(idx)) return prev;
      const next = new Map(prev);
      next.delete(idx);
      return next;
    });
    if (addIngFor === idx) {
      setAddIngFor(null);
      setAddIngText("");
      setAddIngFailedAi(false);
    }
    // Mirror cleanup for the per-flat-item swap panel: a dangling itemSwapFor
    // pointing at a removed idx would render the panel against the next-row
    // shifted item on re-render. Same intent as the addIngFor branch above.
    if (itemSwapFor === idx) {
      setItemSwapFor(null);
      setItemSwapText("");
      setItemSwapFailedAi(false);
    }
    // (swappedIdxRef shift handled above before the early returns; the
    // removed idx is dropped and the higher indices are decremented.)
  }

  // Applies a partial edit to one detected item (name, portion, macros).
  function updateItem(idx: number, patch: Partial<Item>) {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }

  // After a flat item's portion was edited, rescale its kcal/macros by the
  // net change (`base` = portion captured on focus).
  function rescaleItemPortion(idx: number, base: number | null) {
    if (base === null || base <= 0) return;
    setItems((prev) =>
      prev.map((it, i) => {
        if (i !== idx) return it;
        const cur = it.portionGrams;
        if (cur === null || cur <= 0 || cur === base) return it;
        return rescaleNutrition(it, cur / base);
      }),
    );
  }

  // Same for an ingredient — then re-sum the dish.
  function rescaleIngredientPortion(itemIdx: number, ingIdx: number, base: number | null) {
    if (base === null || base <= 0) return;
    setItems((prev) =>
      prev.map((it, i) => {
        if (i !== itemIdx || !it.ingredients) return it;
        const ings = it.ingredients.map((g, j) => {
          if (j !== ingIdx) return g;
          const cur = g.portionGrams;
          if (cur === null || cur <= 0 || cur === base) return g;
          return rescaleNutrition(g, cur / base);
        });
        return { ...it, ingredients: ings, ...dishTotals(ings) };
      }),
    );
  }

  // Parses a numeric field input. Empty → null (macros/portion may be
  // unknown). NaN guarded. kcal is clamped to a sane integer range.
  function parseNum(raw: string, opts: { int?: boolean; min: number; max: number }): number | null {
    if (raw.trim() === "") return null;
    const n = Number(raw.replace(",", "."));
    if (!Number.isFinite(n)) return null;
    const clamped = Math.max(opts.min, Math.min(opts.max, n));
    return opts.int ? Math.round(clamped) : clamped;
  }

  // Edits one ingredient of a dish and re-sums the dish total (ADR-0010).
  function updateIngredient(itemIdx: number, ingIdx: number, patch: Partial<Ingredient>) {
    setItems((prev) =>
      prev.map((it, i) => {
        if (i !== itemIdx || !it.ingredients) return it;
        const ings = it.ingredients.map((g, j) => (j === ingIdx ? { ...g, ...patch } : g));
        return { ...it, ingredients: ings, ...dishTotals(ings) };
      }),
    );
  }

  function removeIngredient(itemIdx: number, ingIdx: number) {
    setItems((prev) =>
      prev.map((it, i) => {
        if (i !== itemIdx || !it.ingredients) return it;
        const ings = it.ingredients.filter((_, j) => j !== ingIdx);
        return { ...it, ingredients: ings, ...dishTotals(ings) };
      }),
    );
    setSwap(null);
  }

  // Swap an ingredient: re-estimate the new name via the text parser and
  // splice its nutrition in, then the dish total re-sums automatically.
  async function applySwap() {
    if (!swap) return;
    const name = swap.text.trim();
    if (name.length < 2) return;
    setSwapping(true);
    setError(null);
    try {
      const parsed = await api.parseMeal(name);
      const est = parsed.items[0];
      if (!est) {
        setError(t.confirmGenericError);
        return;
      }
      updateIngredient(swap.itemIdx, swap.ingIdx, {
        name: est.name,
        kcal: est.kcal,
        proteinG: est.proteinG,
        fatG: est.fatG,
        carbsG: est.carbsG,
        portionGrams: est.portionGrams,
      });
      setSwap(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.confirmGenericError);
    } finally {
      setSwapping(false);
    }
  }

  // Swap an entire FLAT item: re-parse the user's new name via Syto Sense
  // and REPLACE items[idx] with the fresh estimate. Use case: AI parsed
  // "Jajka kurze surowe" but the user wants "Jajka gotowane", and they get
  // a proper kcal/macro re-estimate instead of editing five fields by hand.
  //
  // Guards by construction (called only from the FLAT branch, chip is
  // hidden when item.ingredients?.length > 0). REPLACE only items[itemIdx];
  // never touches other indices. parsed.source is per-meal not per-item,
  // and is intentionally NOT altered here (mirrors addMissingItem).
  //
  // Telemetry: marks itemIdx in swappedIdxRef so handleSave skips it (the
  // swapped item's name no longer corresponds to any parsed.items[].name
  // semantically; an incidental collision with another parsed item's name
  // would emit a misleading aiKcal feedback row).
  async function applyItemSwap(itemIdx: number) {
    const text = itemSwapText.trim();
    if (text.length < 2) return;
    if (itemSwapping) return;
    const now = Date.now();
    if (now - lastItemSwapAtRef.current < ITEM_SWAP_THROTTLE_MS) return;
    lastItemSwapAtRef.current = now;
    setItemSwapping(true);
    setError(null);
    try {
      const parsed = await api.parseMeal(text);
      const est = parsed.items[0];
      if (!est) {
        setError(t.confirmItemSwapEmpty);
        return;
      }
      // Multi-item parses (e.g. user types "kawa i bułka" into the swap
      // field) silently use items[0]. Swap is 1:1 replace; multi doesn't
      // make sense here. Mirrors applySwap (the ingredient-level swap).
      //
      // Build the FULL new item (NOT a partial patch). updateItem's spread
      // merge would carry stale `assumptions` / `portionUnit` from the
      // pre-swap item when the new estimate lacks them. Conditional spread
      // mirrors addMissingItem (lines around 350); confidence is taken
      // verbatim from the parser (do NOT bump to 1.0, that would lie to
      // the Conf chip and pollute AI-feedback analytics). `ingredients`
      // is deliberately omitted: a swap output stays FLAT even if the AI
      // returned a decomposition (the source row was flat by guard).
      const replaced: Item = {
        name: est.name,
        kcal: est.kcal,
        proteinG: est.proteinG,
        fatG: est.fatG,
        carbsG: est.carbsG,
        portionGrams: est.portionGrams,
        confidence: est.confidence,
        ...(est.assumptions !== null && est.assumptions !== undefined
          ? { assumptions: est.assumptions }
          : {}),
        ...(est.portionUnit ? { portionUnit: est.portionUnit } : {}),
      };
      setItems((prev) => prev.map((it, i) => (i === itemIdx ? replaced : it)));
      // Keep the inline editor open on the same row so the user immediately
      // sees the new name/kcal/macros in the fields they just edited. Do
      // NOT call setEditingIdx(null): the appended-row pattern from
      // addMissingItem does not apply (we are at a known idx, not new tail).
      setEditingIdx(itemIdx);
      setItemSwapFor(null);
      setItemSwapText("");
      setItemSwapFailedAi(false);
      // Mutual exclusion of fallback chips (mirrors addMissingItem /
      // addIngredient success paths): only one fallback affordance lives
      // in the error pill at a time.
      setAddFailedAi(false);
      setAddIngFailedAi(false);
      // The Favorite chip on this row was saved against the OLD food; the
      // swapped item is a different food. Clear so the user can re-favorite
      // the new name if they want.
      setSavedFavIdx((prev) => {
        if (!prev.has(itemIdx)) return prev;
        const next = new Set(prev);
        next.delete(itemIdx);
        return next;
      });
      swappedIdxRef.current.add(itemIdx);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.confirmItemSwapError);
      // Mark the AI path as failed so the error pill surfaces the manual
      // fallback chip (rate-limit / aiGuard / 5xx all land here).
      setItemSwapFailedAi(true);
    } finally {
      setItemSwapping(false);
    }
  }

  // Fallback when the AI swap path fails (rate-limit, aiGuard, 5xx): REPLACE
  // items[itemIdx] with a minimal placeholder carrying the user's literal
  // text and zero kcal / null macros / confidence 0. Bypasses the throttle
  // (no AI call). Same telemetry-skip rule as the AI path.
  function applyItemSwapManual(itemIdx: number) {
    const name = itemSwapText.trim();
    if (name.length < 1) return;
    const replaced: Item = {
      name: name.slice(0, 200),
      kcal: 0,
      proteinG: null,
      fatG: null,
      carbsG: null,
      portionGrams: null,
      // 0 confidence matches the truth: user assertion with no estimate
      // behind it. The Conf chip will reflect that. `assumptions` and
      // `portionUnit` are intentionally omitted (object keys absent), and
      // `ingredients` is dropped (same flatness rule as the AI path).
      confidence: 0,
    };
    setItems((prev) => prev.map((it, i) => (i === itemIdx ? replaced : it)));
    setEditingIdx(itemIdx);
    setItemSwapFor(null);
    setItemSwapText("");
    setError(null);
    setItemSwapFailedAi(false);
    setAddFailedAi(false);
    setAddIngFailedAi(false);
    setSavedFavIdx((prev) => {
      if (!prev.has(itemIdx)) return prev;
      const next = new Set(prev);
      next.delete(itemIdx);
      return next;
    });
    swappedIdxRef.current.add(itemIdx);
  }

  // Append-only "add a missing item" via Syto Sense text parse. Reuses the
  // same parseMeal endpoint as the entry screen + ingredient swap, so cost,
  // schema and validation are already in place (no new server surface).
  //
  // Why append-only at the end: the items list uses `key={idx}` which is
  // stable as long as we don't reorder or insert in the middle — appending
  // preserves focus on every existing row, including one mid-edit.
  //
  // Source inheritance note: a manually-added item ends up saved with the
  // session-level `source` ('photo' for a photo-confirmation, etc.) because
  // MealCreateSchema stores source per-meal, not per-item. This is by design
  // in the schema — the SESSION has a source, not each item.
  async function addMissingItem() {
    const text = addText.trim();
    // Same min-length guard as applySwap (MealParseInputSchema.min(2)).
    if (text.length < 2) return;
    if (adding) return;
    if (addCount >= MAX_MANUAL_ADDS) {
      setError(t.confirmAddItemLimit);
      return;
    }
    const now = Date.now();
    if (now - lastAddAtRef.current < ADD_THROTTLE_MS) return;
    lastAddAtRef.current = now;
    setAdding(true);
    setError(null);
    try {
      const parsed = await api.parseMeal(text);
      const ests = parsed.items;
      if (ests.length === 0) {
        setError(t.confirmAddItemEmpty);
        return;
      }
      // Flatten each parsed item: strip `ingredients` (we keep manual adds
      // as flat single items; mixing dish-decomposition into a manual add
      // would break the ADR-0010 dishTotals invariant — the inline editor
      // already supports flat items end-to-end). Preserve `assumptions`
      // when the parser returned it (schema accepts it for any item type,
      // and it's a real signal for the user, e.g. "założono pszenny chleb").
      // `confidence` is taken verbatim from the parser — typically 0.6-0.9
      // for text-input; do NOT bump to 1.0 (would lie to the Conf chip and
      // pollute the AI-feedback analytics).
      const newItems: Item[] = ests.map((est) => ({
        name: est.name,
        kcal: est.kcal,
        proteinG: est.proteinG,
        fatG: est.fatG,
        carbsG: est.carbsG,
        portionGrams: est.portionGrams,
        confidence: est.confidence,
        ...(est.assumptions !== null && est.assumptions !== undefined
          ? { assumptions: est.assumptions }
          : {}),
        ...(est.portionUnit ? { portionUnit: est.portionUnit } : {}),
      }));
      // Server-side MealCreateSchema caps items at 20. Refuse the append
      // outright (rather than silently truncate) so the user knows what
      // happened and can save+restart for the rest.
      if (items.length + newItems.length > MAX_ITEMS) {
        setError(t.confirmAddItemLimit);
        setAddFailedAi(false);
        return;
      }
      // Capture pre-append length: this is exactly the index of the FIRST
      // newly-appended item. Setting editingIdx to this opens the inline
      // editor on the first new row (not the last) so users typing
      // "kawa i bułka" land in the first item.
      // Anti-pattern guard: do NOT call setEditingIdx inside the setItems
      // updater (side effect in a reducer, breaks under StrictMode and
      // concurrent rendering). Use the captured `items.length` instead,
      // which is the pre-append length by definition.
      const firstNewIdx = items.length;
      setItems((prev) => [...prev, ...newItems]);
      setEditingIdx(firstNewIdx);
      setAddCount((n) => n + 1);
      setAddText("");
      setAddOpen(false);
      setAddFailedAi(false);
      // Mutual exclusion of fallback chips (review issues #1 across two
      // workflows): a successful item-add clears the OTHER TWO recovery
      // flags so the error pill never shows multiple fallbacks together.
      setAddIngFailedAi(false);
      setItemSwapFailedAi(false);
      setAddNotice(newItems.length > 1 ? t.confirmAddItemMulti(newItems.length) : null);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.confirmAddItemError);
      // Mark the AI path as failed so the error pill can offer a manual
      // fallback ("wpisz ręcznie") — rate-limit / aiGuard / 5xx all land
      // here and would otherwise block the user entirely.
      setAddFailedAi(true);
    } finally {
      setAdding(false);
    }
  }

  // Last-resort fallback when the AI parse path fails (rate-limit, aiGuard
  // block, network). Appends a minimal flat item with the user's literal
  // text as the name and zero macros, then opens the inline editor on it
  // so the user can fill in kcal / portion / macros by hand. Bypasses the
  // throttle (no AI call). Still respects MAX_MANUAL_ADDS.
  function addMissingItemManual() {
    const name = addText.trim();
    if (name.length < 1) return;
    if (addCount >= MAX_MANUAL_ADDS) {
      setError(t.confirmAddItemLimit);
      return;
    }
    // Server-side max(20) — same guard as the AI path.
    if (items.length >= MAX_ITEMS) {
      setError(t.confirmAddItemLimit);
      return;
    }
    const fallbackItem: Item = {
      name: name.slice(0, 200),
      kcal: 0,
      proteinG: null,
      fatG: null,
      carbsG: null,
      portionGrams: null,
      // 0 confidence matches the truth: this is a user assertion with no
      // estimate behind it. The Conf chip will reflect that.
      confidence: 0,
    };
    const firstNewIdx = items.length;
    setItems((prev) => [...prev, fallbackItem]);
    setEditingIdx(firstNewIdx);
    setAddCount((n) => n + 1);
    setAddText("");
    setAddOpen(false);
    setError(null);
    setAddFailedAi(false);
    setAddNotice(null);
  }

  // Append-only "add a missing ingredient" via Syto Sense text parse.
  // Mirrors addMissingItem one-to-one: same parseMeal endpoint, same multi
  // handling, same throttle and cap pattern. The only differences are:
  // (a) it's per-dish, scoped by itemIdx; (b) parser output is reshaped to
  // IngredientSchema (no confidence / assumptions / portionUnit); (c) the
  // dish-total invariant from dishTotals(ings) is re-applied after the
  // append (ADR-0010, item.{kcal,proteinG,fatG,carbsG} = sum(ingredients)).
  async function addIngredient(itemIdx: number) {
    const text = addIngText.trim();
    if (text.length < 2) return;
    if (addingIng) return;
    const dishCount = addIngCount.get(itemIdx) ?? 0;
    if (dishCount >= MAX_INGREDIENT_ADDS) {
      setError(t.confirmAddIngLimit);
      return;
    }
    const now = Date.now();
    if (now - lastIngAddAtRef.current < ADD_ING_THROTTLE_MS) return;
    lastIngAddAtRef.current = now;
    setAddingIng(true);
    setError(null);
    try {
      const parsed = await api.parseMeal(text);
      const ests = parsed.items;
      if (ests.length === 0) {
        setError(t.confirmAddIngEmpty);
        return;
      }
      // Reshape each parsed MealItem into an Ingredient. Explicitly drop
      // `confidence`, `assumptions`, `portionUnit`: they are NOT on
      // IngredientSchema, and a blind spread would round-trip-fail the Zod
      // validator. All six Ingredient fields must be PRESENT, with `null`
      // for unknown macros (the schema is nullable, not optional).
      const newIngs: Ingredient[] = ests.map((est) => ({
        name: est.name,
        kcal: est.kcal,
        proteinG: est.proteinG,
        fatG: est.fatG,
        carbsG: est.carbsG,
        portionGrams: est.portionGrams,
      }));
      const existing = items[itemIdx]?.ingredients ?? [];
      if (existing.length + newIngs.length > MAX_INGREDIENTS) {
        setError(t.confirmAddIngLimit);
        setAddIngFailedAi(false);
        return;
      }
      // ADR-0010 invariant: compute the merged array ONCE, then pass to
      // dishTotals, never re-sum on a stale reference.
      setItems((prev) =>
        prev.map((it, i) => {
          if (i !== itemIdx) return it;
          const merged = [...(it.ingredients ?? []), ...newIngs];
          return { ...it, ingredients: merged, ...dishTotals(merged) };
        }),
      );
      setAddIngCount((prev) => {
        // Read prev.get inside the updater so concurrent dispatches don't
        // lose a count (review issue #4 — parallels setAddCount((n) => n+1)).
        const next = new Map(prev);
        next.set(itemIdx, (next.get(itemIdx) ?? 0) + 1);
        return next;
      });
      setAddIngText("");
      setAddIngFor(null);
      setAddIngFailedAi(false);
      // Mutual exclusion of fallback chips (review issues #1 across two
      // workflows): a successful ingredient-add clears the OTHER TWO
      // recovery flags so the user is not staring at multiple recovery
      // affordances inside one red pill.
      setAddFailedAi(false);
      setItemSwapFailedAi(false);
      setAddIngNotice(newIngs.length > 1 ? t.confirmAddIngMulti(newIngs.length) : null);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.confirmAddIngError);
      // Mark the AI path as failed so the error pill surfaces the manual
      // fallback chip (rate-limit / aiGuard / 5xx all land here).
      setAddIngFailedAi(true);
    } finally {
      setAddingIng(false);
    }
  }

  // Fallback when the AI parse path fails: append a zero-kcal Ingredient
  // placeholder with the user's literal text. Bypasses AI/throttle, still
  // respects MAX_INGREDIENT_ADDS and MAX_INGREDIENTS. The user can then
  // edit kcal/macros via the existing per-ingredient editor (the row opens
  // automatically because its parent dish is already in editingIdx).
  function addIngredientManual(itemIdx: number) {
    const name = addIngText.trim();
    if (name.length < 1) return;
    const dishCount = addIngCount.get(itemIdx) ?? 0;
    if (dishCount >= MAX_INGREDIENT_ADDS) {
      setError(t.confirmAddIngLimit);
      return;
    }
    const existing = items[itemIdx]?.ingredients ?? [];
    if (existing.length >= MAX_INGREDIENTS) {
      setError(t.confirmAddIngLimit);
      return;
    }
    const fallbackIng: Ingredient = {
      name: name.slice(0, 200),
      kcal: 0,
      proteinG: null,
      fatG: null,
      carbsG: null,
      portionGrams: null,
    };
    setItems((prev) =>
      prev.map((it, i) => {
        if (i !== itemIdx) return it;
        const merged = [...(it.ingredients ?? []), fallbackIng];
        return { ...it, ingredients: merged, ...dishTotals(merged) };
      }),
    );
    setAddIngCount((prev) => {
      const next = new Map(prev);
      next.set(itemIdx, dishCount + 1);
      return next;
    });
    setAddIngText("");
    setAddIngFor(null);
    setError(null);
    setAddIngFailedAi(false);
    setAddIngNotice(null);
  }

  // Lets the user nudge a too-low / too-high AI kcal estimate before
  // saving. The delta vs the original estimate is sent as feedback.
  function adjustKcal(idx: number, delta: number) {
    setItems((prev) =>
      prev.map((it, i) =>
        i === idx ? { ...it, kcal: Math.max(0, Math.min(10000, it.kcal + delta)) } : it,
      ),
    );
  }

  // Maps the parsed source to the telemetry endpoint label.
  function feedbackEndpoint(): "meal-photo" | "meal-voice" | "meal-barcode" | "meal-parse" {
    switch (parsed.source) {
      case "photo":
        return "meal-photo";
      case "voice":
        return "meal-voice";
      case "barcode":
        return "meal-barcode";
      default:
        return "meal-parse";
    }
  }

  async function saveAsFavorite(item: Item, idx: number) {
    setSavingFavIdx(idx);
    setError(null);
    try {
      await api.createFavorite(householdId, {
        name: item.name,
        kcal: item.kcal,
        proteinG: item.proteinG,
        fatG: item.fatG,
        carbsG: item.carbsG,
        portionGrams: item.portionGrams,
      });
      setSavedFavIdx((prev) => new Set(prev).add(idx));
    } catch (err) {
      setError(err instanceof Error ? err.message : t.confirmFavoriteError);
    } finally {
      setSavingFavIdx(null);
    }
  }

  async function handleSave() {
    if (items.length === 0) {
      onCancel();
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.saveMeals(
        {
          items,
          source: parsed.source ?? "manual_ai",
          ...(parsed.rawInputText ? { rawInputText: parsed.rawInputText } : {}),
          ...(active.kind === "child" ? { childId: active.childId } : {}),
        },
        householdId,
      );
      // Fire-and-forget: report kcal corrections vs the AI estimate.
      // Items added via "add a missing item" are intentionally absent from
      // `parsed.items` (the parent parse output), so the name-match below
      // skips them by design — do NOT "fix" this by emitting an aiKcal=0
      // feedback row for them; that would pollute the analytics with a
      // false 100% correction signal. The same goes for items that were
      // SWAPPED via the per-flat-item AI swap: their current name belongs
      // to a different food than the original parse output, so any
      // incidental name-collision with another parsed item would emit a
      // misleading aiKcal row. We track those indices explicitly and skip.
      const endpoint = feedbackEndpoint();
      for (let i = 0; i < items.length; i += 1) {
        if (swappedIdxRef.current.has(i)) continue;
        const fin = items[i];
        if (!fin) continue;
        const ai = parsed.items.find((p) => p.name === fin.name);
        if (ai && ai.kcal !== fin.kcal) {
          void api.sendAiFeedback({
            endpoint,
            name: fin.name,
            aiKcal: ai.kcal,
            finalKcal: fin.kcal,
            aiPortionGrams: ai.portionGrams,
            finalPortionGrams: fin.portionGrams,
          });
        }
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.confirmGenericError);
    } finally {
      setSubmitting(false);
    }
  }

  const count = items.length;
  const macrosTotal = `b${Math.round(totals.proteinG)} · t${Math.round(totals.fatG)} · w${Math.round(totals.carbsG)}`;

  return (
    <ScreenShell hideHeader>
      <TopBar back={t.confirmBack} title={t.confirmTitle} onBack={onCancel} />

      {/* Hero: "N rzeczy." */}
      <div style={{ marginTop: 18 }}>
        <H1 accent=".">
          {count} {t.confirmThingCount(count)}
        </H1>
        <p className="text-[14px] leading-snug" style={{ marginTop: 12, color: "var(--ink-2)" }}>
          <span
            className="tabular"
            style={{
              fontFamily: "var(--font-display)",
              fontWeight: 800,
              color: "var(--ink)",
            }}
          >
            ≈{totals.kcal} kcal
          </span>
          {" · "}
          {macrosTotal}
          {t.confirmCheckHint}
        </p>
      </div>

      {/* Numbered list. */}
      <div style={{ marginTop: 22 }}>
        {items.map((item, idx) => {
          const isFaved = savedFavIdx.has(idx);
          const isFaving = savingFavIdx === idx;
          const portion = portionString(item);
          const macros = macroString(item);
          const metaParts = [portion?.toUpperCase(), macros].filter(Boolean).join(" · ");
          return (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: list is not reordered; a content-based key would remount the row on every keystroke and drop input focus.
              key={idx}
              style={{
                padding: "14px 0",
                borderTop: idx === 0 ? "1px solid var(--nav-border)" : "none",
                borderBottom: "1px solid var(--nav-border)",
              }}
            >
              <div className="flex items-start gap-3.5">
                <div
                  className="shrink-0 tabular"
                  style={{
                    flex: "0 0 22px",
                    fontFamily: "var(--font-mono)",
                    fontSize: 10,
                    color: "var(--muted)",
                    fontWeight: 700,
                    paddingTop: 3,
                    letterSpacing: "0.04em",
                  }}
                >
                  {String(idx + 1).padStart(2, "0")}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2" style={{ marginBottom: 4 }}>
                    <span
                      className="truncate"
                      style={{ fontSize: 15, fontWeight: 700, color: "var(--ink)" }}
                    >
                      {item.name}
                    </span>
                    <Conf level={confLevelFromScore(item.confidence)} />
                  </div>
                  {metaParts ? <MonoMeta>{metaParts}</MonoMeta> : null}
                  {item.assumptions ? (
                    <p
                      style={{
                        fontSize: 11,
                        color: "var(--muted)",
                        marginTop: 3,
                        lineHeight: 1.35,
                      }}
                    >
                      {item.assumptions}
                    </p>
                  ) : null}
                </div>
                <div className="shrink-0" style={{ textAlign: "right" }}>
                  <div
                    className="tabular"
                    style={{
                      fontFamily: "var(--font-display)",
                      fontSize: 22,
                      fontWeight: 700,
                      color: "var(--ink)",
                      letterSpacing: "-0.02em",
                    }}
                  >
                    {item.kcal}
                  </div>
                  {/* kcal steppers — hidden for dishes (kcal = ingredient sum). */}
                  {!item.ingredients?.length ? (
                    <div
                      className="flex items-center gap-1"
                      style={{ justifyContent: "flex-end", marginTop: 3 }}
                    >
                      <button
                        type="button"
                        onClick={() => adjustKcal(idx, -25)}
                        aria-label={t.confirmLessKcalAria(item.name)}
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: 6,
                          background: "transparent",
                          color: "var(--ink-2)",
                          boxShadow: "inset 0 0 0 1px var(--nav-border)",
                          border: "none",
                          fontSize: 14,
                          fontWeight: 700,
                          lineHeight: 1,
                        }}
                      >
                        −
                      </button>
                      <button
                        type="button"
                        onClick={() => adjustKcal(idx, 25)}
                        aria-label={t.confirmMoreKcalAria(item.name)}
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: 6,
                          background: "transparent",
                          color: "var(--ink-2)",
                          boxShadow: "inset 0 0 0 1px var(--nav-border)",
                          border: "none",
                          fontSize: 14,
                          fontWeight: 700,
                          lineHeight: 1,
                        }}
                      >
                        +
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
              {/* Per-item action chips. */}
              <div className="flex gap-2" style={{ marginTop: 10, marginLeft: 36 }}>
                <button
                  type="button"
                  onClick={() => setEditingIdx(editingIdx === idx ? null : idx)}
                  aria-expanded={editingIdx === idx}
                  className="inline-flex items-center gap-1"
                  style={{
                    padding: "5px 10px",
                    fontSize: 11,
                    fontWeight: 600,
                    color: editingIdx === idx ? "var(--accent)" : "var(--ink-2)",
                    boxShadow: "inset 0 0 0 1px var(--nav-border)",
                    borderRadius: 6,
                    background: "transparent",
                    border: "none",
                  }}
                >
                  {editingIdx === idx ? t.confirmEditDone : t.confirmEdit}
                </button>
                <button
                  type="button"
                  onClick={() => saveAsFavorite(item, idx)}
                  disabled={isFaved || isFaving}
                  className="inline-flex items-center gap-1 disabled:opacity-60"
                  style={{
                    padding: "5px 10px",
                    fontSize: 11,
                    fontWeight: 600,
                    color: isFaved ? "var(--accent)" : "var(--ink-2)",
                    boxShadow: "inset 0 0 0 1px var(--nav-border)",
                    borderRadius: 6,
                    background: "transparent",
                    border: "none",
                  }}
                >
                  <IconStar size={12} filled={isFaved} />
                  {isFaved ? t.confirmFaved : isFaving ? t.confirmFaving : t.confirmFavorite}
                </button>
                {/* Per-flat-item AI swap chip. HIDDEN for dishes
                    (item.ingredients?.length > 0), because dishes have
                    per-ingredient swap already and offering both at the
                    item AND ingredient
                    level on the same row would be ambiguous (replace what:
                    the whole dish, blowing away ingredients, or one
                    ingredient?). Construction-guarded: chip + panel + handler
                    all sit in the FLAT branch. */}
                {itemSwapEnabled && !item.ingredients?.length ? (
                  <button
                    type="button"
                    onClick={() => {
                      setError(null);
                      setItemSwapText("");
                      setItemSwapFailedAi(false);
                      const next = itemSwapFor === idx ? null : idx;
                      setItemSwapFor(next);
                      // HIGH review issue: the swap input panel renders
                      // inside `editingIdx === idx ? (...)`, so without
                      // opening the editor the chip would turn accent but
                      // no panel would appear. Pop the editor open when
                      // the user activates swap on a closed row.
                      if (next !== null) setEditingIdx(idx);
                    }}
                    aria-expanded={itemSwapFor === idx}
                    aria-label={t.confirmItemSwapAria(item.name)}
                    className="inline-flex items-center gap-1"
                    style={{
                      padding: "5px 10px",
                      fontSize: 11,
                      fontWeight: 600,
                      color: itemSwapFor === idx ? "var(--accent)" : "var(--ink-2)",
                      boxShadow: "inset 0 0 0 1px var(--nav-border)",
                      borderRadius: 6,
                      background: "transparent",
                      border: "none",
                    }}
                  >
                    {t.confirmItemSwap}
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => removeItem(idx)}
                  aria-label={t.confirmRemoveAria(item.name)}
                  style={{
                    padding: "5px 10px",
                    fontSize: 11,
                    fontWeight: 600,
                    color: "var(--muted)",
                    background: "transparent",
                    border: "none",
                  }}
                >
                  {t.confirmRemove}
                </button>
              </div>

              {/* Inline editor — full control over the detected item. */}
              {editingIdx === idx ? (
                <div
                  className="flex flex-col gap-2"
                  style={{ marginTop: 12, marginLeft: 36 }}
                >
                  {/* In swap mode the user is REPLACING the whole item
                      (name + macros + portion via parser). Editing fields
                      simultaneously is confusing — hide them while
                      itemSwapFor points here. They reappear after the swap
                      lands with the fresh values populated. */}
                  {itemSwapFor !== idx ? (
                    <LabeledInput
                      label={t.confirmFieldName}
                      value={item.name}
                      onChange={(v) => updateItem(idx, { name: v.slice(0, 200) })}
                    />
                  ) : null}

                  {itemSwapFor !== idx && item.ingredients && item.ingredients.length > 0 ? (
                    /* Dish → editable ingredient list; dish total = sum. */
                    <>
                      <span
                        style={{
                          fontFamily: "var(--font-mono)",
                          fontSize: 9,
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                          color: "var(--muted)",
                          fontWeight: 600,
                          marginTop: 4,
                        }}
                      >
                        {t.confirmIngredientsLabel}
                      </span>
                      {item.ingredients.map((ing, j) => {
                        const swapActive =
                          swap !== null && swap.itemIdx === idx && swap.ingIdx === j;
                        return (
                          <div
                            // biome-ignore lint/suspicious/noArrayIndexKey: list is not reordered; a content-based key would remount the row on every keystroke and drop input focus.
                            key={j}
                            className="flex flex-col gap-1.5"
                            style={{
                              padding: 10,
                              borderRadius: 10,
                              background: "var(--surface-2)",
                            }}
                          >
                            <LabeledInput
                              label={t.confirmFieldName}
                              value={ing.name}
                              onChange={(v) =>
                                updateIngredient(idx, j, { name: v.slice(0, 200) })
                              }
                            />
                            <div className="grid grid-cols-3 gap-2">
                              <LabeledInput
                                label={t.confirmFieldPortion}
                                value={ing.portionGrams ?? ""}
                                numeric
                                onFocus={() => {
                                  portionBaseRef.current = ing.portionGrams;
                                }}
                                onChange={(v) =>
                                  updateIngredient(idx, j, {
                                    portionGrams: parseNum(v, { min: 0, max: 5000 }),
                                  })
                                }
                                onBlur={() =>
                                  rescaleIngredientPortion(idx, j, portionBaseRef.current)
                                }
                              />
                              <LabeledInput
                                label={t.confirmFieldKcal}
                                value={ing.kcal}
                                numeric
                                onChange={(v) =>
                                  updateIngredient(idx, j, {
                                    kcal: parseNum(v, { int: true, min: 0, max: 10000 }) ?? 0,
                                  })
                                }
                              />
                              <LabeledInput
                                label={t.confirmFieldProtein}
                                value={ing.proteinG ?? ""}
                                numeric
                                onChange={(v) =>
                                  updateIngredient(idx, j, {
                                    proteinG: parseNum(v, { min: 0, max: 500 }),
                                  })
                                }
                              />
                              <LabeledInput
                                label={t.confirmFieldFat}
                                value={ing.fatG ?? ""}
                                numeric
                                onChange={(v) =>
                                  updateIngredient(idx, j, {
                                    fatG: parseNum(v, { min: 0, max: 500 }),
                                  })
                                }
                              />
                              <LabeledInput
                                label={t.confirmFieldCarbs}
                                value={ing.carbsG ?? ""}
                                numeric
                                onChange={(v) =>
                                  updateIngredient(idx, j, {
                                    carbsG: parseNum(v, { min: 0, max: 1000 }),
                                  })
                                }
                              />
                            </div>
                            {swapActive ? (
                              <div className="flex gap-2 items-end">
                                <div style={{ flex: 1 }}>
                                  <LabeledInput
                                    label={t.confirmSwap}
                                    value={swap.text}
                                    onChange={(v) =>
                                      setSwap({ itemIdx: idx, ingIdx: j, text: v })
                                    }
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={applySwap}
                                  disabled={swapping}
                                  className="disabled:opacity-50"
                                  style={{
                                    height: 38,
                                    padding: "0 12px",
                                    borderRadius: 8,
                                    background: "var(--accent)",
                                    color: "var(--accent-ink)",
                                    border: "none",
                                    fontSize: 11,
                                    fontWeight: 700,
                                  }}
                                >
                                  {swapping ? t.confirmSwapping : t.confirmSwapApply}
                                </button>
                              </div>
                            ) : (
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => setSwap({ itemIdx: idx, ingIdx: j, text: "" })}
                                  style={{
                                    padding: "5px 10px",
                                    fontSize: 11,
                                    fontWeight: 600,
                                    color: "var(--ink-2)",
                                    boxShadow: "inset 0 0 0 1px var(--nav-border)",
                                    borderRadius: 6,
                                    background: "transparent",
                                    border: "none",
                                  }}
                                >
                                  {t.confirmSwap}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => removeIngredient(idx, j)}
                                  style={{
                                    padding: "5px 10px",
                                    fontSize: 11,
                                    fontWeight: 600,
                                    color: "var(--muted)",
                                    background: "transparent",
                                    border: "none",
                                  }}
                                >
                                  {t.confirmRemove}
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {/* "Add a missing ingredient", mirrors the top-level
                          chip but lives below the ingredient list of THIS
                          dish. The chip only renders here, so the
                          flat-to-dish promotion edge case (data-destructive
                          via dishTotals overwriting item.kcal) is avoided
                          by construction. */}
                      {addIngEnabled ? (
                        <div style={{ marginTop: 4 }}>
                          {addIngFor !== idx ? (
                            <button
                              type="button"
                              onClick={() => {
                                setError(null);
                                setAddIngText("");
                                setAddIngFor(idx);
                              }}
                              disabled={
                                (addIngCount.get(idx) ?? 0) >= MAX_INGREDIENT_ADDS ||
                                (item.ingredients?.length ?? 0) >= MAX_INGREDIENTS
                              }
                              className="inline-flex items-center gap-1 disabled:opacity-50"
                              style={{
                                padding: "5px 10px",
                                fontSize: 11,
                                fontWeight: 600,
                                color: "var(--ink-2)",
                                boxShadow: "inset 0 0 0 1px var(--nav-border)",
                                borderRadius: 6,
                                background: "transparent",
                                border: "none",
                              }}
                            >
                              + {t.confirmAddIng}
                            </button>
                          ) : (
                            <div className="flex flex-col gap-2">
                              <div className="flex gap-2 items-end">
                                <div style={{ flex: 1 }}>
                                  <LabeledInput
                                    label={t.confirmAddIngLabel}
                                    value={addIngText}
                                    onChange={(v) => setAddIngText(v.slice(0, 200))}
                                    placeholder={t.confirmAddIngPlaceholder}
                                    onSubmit={() => addIngredient(idx)}
                                    disabled={addingIng}
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={() => addIngredient(idx)}
                                  disabled={addingIng || addIngText.trim().length < 2}
                                  className="disabled:opacity-50"
                                  style={{
                                    height: 38,
                                    padding: "0 14px",
                                    borderRadius: 8,
                                    background: "var(--accent)",
                                    color: "var(--accent-ink)",
                                    border: "none",
                                    fontSize: 11,
                                    fontWeight: 700,
                                  }}
                                >
                                  {addingIng ? t.confirmAddIngPending : t.confirmAddIngSubmit}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setAddIngText("");
                                    setAddIngFor(null);
                                    setAddIngFailedAi(false);
                                  }}
                                  disabled={addingIng}
                                  className="disabled:opacity-50"
                                  style={{
                                    height: 38,
                                    padding: "0 10px",
                                    fontSize: 11,
                                    fontWeight: 600,
                                    color: "var(--muted)",
                                    background: "transparent",
                                    border: "none",
                                  }}
                                >
                                  {t.confirmAddIngCancel}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : null}
                      <MonoMeta>
                        {t.confirmDishSum}: {item.kcal} kcal
                      </MonoMeta>
                    </>
                  ) : (
                    /* Flat item → direct field editing. */
                    <>
                      {/* Per-flat-item AI swap panel. Renders only when the
                          chip in the controls row is active (itemSwapFor ===
                          idx). Lives ABOVE the field grid so the user sees
                          "where the new values will land" right below it. */}
                      {itemSwapEnabled && itemSwapFor === idx ? (
                        <div className="flex gap-2 items-end">
                          <div style={{ flex: 1 }}>
                            <LabeledInput
                              label={t.confirmItemSwapLabel}
                              value={itemSwapText}
                              onChange={(v) => setItemSwapText(v.slice(0, 200))}
                              placeholder={t.confirmItemSwapPlaceholder}
                              onSubmit={() => applyItemSwap(idx)}
                              disabled={itemSwapping}
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => applyItemSwap(idx)}
                            disabled={itemSwapping || itemSwapText.trim().length < 2}
                            className="disabled:opacity-50"
                            style={{
                              height: 38,
                              padding: "0 14px",
                              borderRadius: 8,
                              background: "var(--accent)",
                              color: "var(--accent-ink)",
                              border: "none",
                              fontSize: 11,
                              fontWeight: 700,
                            }}
                          >
                            {itemSwapping ? t.confirmItemSwapPending : t.confirmItemSwapSubmit}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setItemSwapText("");
                              setItemSwapFor(null);
                              setItemSwapFailedAi(false);
                            }}
                            disabled={itemSwapping}
                            className="disabled:opacity-50"
                            style={{
                              height: 38,
                              padding: "0 10px",
                              fontSize: 11,
                              fontWeight: 600,
                              color: "var(--muted)",
                              background: "transparent",
                              border: "none",
                            }}
                          >
                            {t.confirmItemSwapCancel}
                          </button>
                        </div>
                      ) : null}
                      {/* Field grid hidden in swap mode (parallel to the
                          name field above) — the user is replacing the
                          whole item, not tweaking its fields. After swap
                          lands, itemSwapFor returns to null and the grid
                          reappears with the new parser values. */}
                      {itemSwapFor === idx ? null : (
                      <div className="grid grid-cols-3 gap-2">
                        <LabeledInput
                          label={t.confirmFieldPortion}
                          value={item.portionGrams ?? ""}
                          numeric
                          onFocus={() => {
                            portionBaseRef.current = item.portionGrams;
                          }}
                          onChange={(v) =>
                            updateItem(idx, { portionGrams: parseNum(v, { min: 0, max: 5000 }) })
                          }
                          onBlur={() => rescaleItemPortion(idx, portionBaseRef.current)}
                        />
                        <LabeledInput
                          label={t.confirmFieldKcal}
                          value={item.kcal}
                          numeric
                          onChange={(v) =>
                            updateItem(idx, {
                              kcal: parseNum(v, { int: true, min: 0, max: 10000 }) ?? 0,
                            })
                          }
                        />
                        <LabeledInput
                          label={t.confirmFieldProtein}
                          value={item.proteinG ?? ""}
                          numeric
                          onChange={(v) =>
                            updateItem(idx, { proteinG: parseNum(v, { min: 0, max: 500 }) })
                          }
                        />
                        <LabeledInput
                          label={t.confirmFieldFat}
                          value={item.fatG ?? ""}
                          numeric
                          onChange={(v) =>
                            updateItem(idx, { fatG: parseNum(v, { min: 0, max: 500 }) })
                          }
                        />
                        <LabeledInput
                          label={t.confirmFieldCarbs}
                          value={item.carbsG ?? ""}
                          numeric
                          onChange={(v) =>
                            updateItem(idx, { carbsG: parseNum(v, { min: 0, max: 1000 }) })
                          }
                        />
                      </div>
                      )}
                    </>
                  )}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      {/* "Add a missing item" — single-input affordance gated by ADR-0017
          flag. Collapsed as a chip by default to keep the resting state
          quiet, expands to an input row on tap. */}
      {addEnabled ? (
        <div style={{ marginTop: 14 }}>
          {!addOpen ? (
            <button
              type="button"
              onClick={() => {
                setError(null);
                setAddOpen(true);
              }}
              disabled={addCount >= MAX_MANUAL_ADDS}
              className="inline-flex items-center gap-1 disabled:opacity-50"
              style={{
                padding: "6px 12px",
                fontSize: 11,
                fontWeight: 600,
                color: "var(--ink-2)",
                boxShadow: "inset 0 0 0 1px var(--nav-border)",
                borderRadius: 8,
                background: "transparent",
                border: "none",
              }}
            >
              + {t.confirmAddItem}
            </button>
          ) : (
            <div className="flex flex-col gap-2">
              <div className="flex gap-2 items-end">
                <div style={{ flex: 1 }}>
                  <LabeledInput
                    label={t.confirmAddItemLabel}
                    value={addText}
                    onChange={(v) => setAddText(v.slice(0, 200))}
                    placeholder={t.confirmAddItemPlaceholder}
                    onSubmit={addMissingItem}
                    disabled={adding}
                  />
                </div>
                <button
                  type="button"
                  onClick={addMissingItem}
                  disabled={adding || addText.trim().length < 2}
                  className="disabled:opacity-50"
                  style={{
                    height: 38,
                    padding: "0 14px",
                    borderRadius: 8,
                    background: "var(--accent)",
                    color: "var(--accent-ink)",
                    border: "none",
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  {adding ? t.confirmAddItemPending : t.confirmAddItemSubmit}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAddText("");
                    setAddOpen(false);
                    setAddFailedAi(false);
                  }}
                  disabled={adding}
                  className="disabled:opacity-50"
                  style={{
                    height: 38,
                    padding: "0 10px",
                    fontSize: 11,
                    fontWeight: 600,
                    color: "var(--muted)",
                    background: "transparent",
                    border: "none",
                  }}
                >
                  {t.confirmAddItemCancel}
                </button>
              </div>
            </div>
          )}
        </div>
      ) : null}

      {/* Transient "added N items" notice after a multi-item parse. */}
      {addNotice ? (
        <p
          className="text-[11px]"
          style={{
            marginTop: 10,
            color: "var(--muted)",
            fontFamily: "var(--font-mono)",
            letterSpacing: "0.04em",
          }}
        >
          {addNotice}
        </p>
      ) : null}

      {/* Parallel notice for multi-ingredient parse on the per-dish chip. */}
      {addIngNotice ? (
        <p
          className="text-[11px]"
          style={{
            marginTop: 10,
            color: "var(--muted)",
            fontFamily: "var(--font-mono)",
            letterSpacing: "0.04em",
          }}
        >
          {addIngNotice}
        </p>
      ) : null}

      {/* AI disclaimer footer. */}
      <AiFooter />

      {error ? (
        <div
          className="text-[12px] px-3 py-2 rounded-md"
          style={{ background: "var(--bad-bg)", color: "var(--bad-fg)", marginTop: 12 }}
        >
          <span>{error}</span>
          {/* Manual fallback link: appears when the AI add-item path failed
              (rate-limit, aiGuard, 5xx). Lets the user salvage their text
              as a flat manual item without another AI call. */}
          {addEnabled && addFailedAi && addText.trim().length >= 1 ? (
            <>
              {" · "}
              <button
                type="button"
                onClick={addMissingItemManual}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "var(--bad-fg)",
                  fontWeight: 700,
                  textDecoration: "underline",
                  cursor: "pointer",
                  padding: 0,
                  fontSize: "inherit",
                }}
              >
                {t.confirmAddItemFallback}
              </button>
            </>
          ) : null}
          {/* Parallel ingredient fallback: appears when the AI add-ingredient
              path failed for the currently-open dish input. Salvages the
              text as a zero-kcal Ingredient placeholder, then the user
              edits kcal/macros via the existing per-ingredient editor. */}
          {addIngEnabled && addIngFailedAi && addIngFor !== null && addIngText.trim().length >= 1 ? (
            <>
              {" · "}
              <button
                type="button"
                onClick={() => {
                  if (addIngFor !== null) addIngredientManual(addIngFor);
                }}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "var(--bad-fg)",
                  fontWeight: 700,
                  textDecoration: "underline",
                  cursor: "pointer",
                  padding: 0,
                  fontSize: "inherit",
                }}
              >
                {t.confirmAddIngFallback}
              </button>
            </>
          ) : null}
          {/* Parallel item-swap fallback: appears when the AI swap path
              failed and the swap panel is still open. Salvages the text as
              a zero-kcal placeholder REPLACING items[idx], so the user can
              fill in kcal/macros via the existing flat-row editor. */}
          {itemSwapEnabled &&
          itemSwapFailedAi &&
          itemSwapFor !== null &&
          itemSwapText.trim().length >= 1 ? (
            <>
              {" · "}
              <button
                type="button"
                onClick={() => {
                  if (itemSwapFor !== null) applyItemSwapManual(itemSwapFor);
                }}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "var(--bad-fg)",
                  fontWeight: 700,
                  textDecoration: "underline",
                  cursor: "pointer",
                  padding: 0,
                  fontSize: "inherit",
                }}
              >
                {t.confirmItemSwapFallback}
              </button>
            </>
          ) : null}
        </div>
      ) : null}

      {/* Sticky CTA. */}
      <button
        type="button"
        onClick={handleSave}
        disabled={submitting || count === 0}
        className="w-full uppercase transition-all disabled:opacity-50 tabular"
        style={{
          marginTop: 18,
          padding: "17px 20px",
          borderRadius: 12,
          background: "var(--accent)",
          color: "var(--accent-ink)",
          fontSize: 14,
          fontWeight: 700,
          letterSpacing: "0.02em",
          boxShadow: "var(--shadow-cta)",
          border: "none",
        }}
      >
        {submitting ? t.confirmSaving : t.confirmSaveCta(count, totals.kcal)}
      </button>
    </ScreenShell>
  );
}

// Compact labeled input for the inline meal-item editor.
function LabeledInput({
  label,
  value,
  onChange,
  onFocus,
  onBlur,
  numeric,
  placeholder,
  onSubmit,
  disabled,
}: {
  label: string;
  value: string | number;
  onChange: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  numeric?: boolean;
  placeholder?: string;
  // Fires on Enter (and Cmd/Ctrl+Enter), useful for one-shot inputs like
  // the "add a missing item" affordance — lets keyboard users skip the
  // submit button.
  onSubmit?: () => void;
  disabled?: boolean;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 9,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "var(--muted)",
          fontWeight: 600,
        }}
      >
        {label}
      </span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        {...(onFocus ? { onFocus } : {})}
        {...(onBlur ? { onBlur } : {})}
        {...(numeric ? { inputMode: "decimal" as const } : {})}
        {...(placeholder ? { placeholder } : {})}
        {...(disabled ? { disabled } : {})}
        {...(onSubmit
          ? {
              onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  onSubmit();
                }
              },
            }
          : {})}
        style={{
          height: 38,
          padding: "0 10px",
          width: "100%",
          background: "var(--surface)",
          color: "var(--ink)",
          borderRadius: 8,
          boxShadow: "inset 0 0 0 1px var(--nav-border)",
          // 16px iOS auto-zoom guard — see primitives.tsx Input.
          fontSize: 16,
          outline: "none",
        }}
      />
    </label>
  );
}
